/**
 * Artifact envelope — the uniform transport for data flowing between LLM
 * functions, chains, and frontend components.
 *
 * The envelope is uniform; payloads are deliberately heterogeneous. A `kind`
 * discriminator (dotted taxonomy aligned with the normalized DB record types)
 * tells consumers what the payload is, `provenance` records where it came
 * from, and `derivedFrom` links artifacts into a lineage graph that powers
 * the transparency UI.
 *
 * This module is isomorphic: imported by server functions, the chain
 * executor, and frontend components. Keep it free of Node and DOM APIs.
 */

import {
  describeArtifactChange,
  historyAfterRevising,
  normalizeRevisionReason,
  type ArtifactChange,
  type ArtifactRevision,
} from "./revisions.ts";
import { isBoundUrlShape, type BoundUrl } from "../security/boundUrl.ts";

export {
  ARTIFACT_CHANGE_EDIT_LIMIT,
  ARTIFACT_REVISION_LIMIT,
  ARTIFACT_REVISION_PAYLOAD_CHARS,
  describeArtifactChange,
  readableUnitsOf,
} from "./revisions.ts";
export type { ArtifactChange, ArtifactEdit, ArtifactRevision } from "./revisions.ts";

export type ArtifactKind = string;

export type ArtifactCitation = {
  id: string;
  title?: string;
  url?: string;
  snippet?: string;
  /** The exact line of the source that supports the claims citing it, as read. */
  quote?: string;
  /** When NV read this source. */
  readAt?: number;
  fileName?: string;
  pageNumber?: number;
};

/**
 * One claim in the payload's prose body and the sources that support it.
 * `start`/`end` are character offsets into `payload.body`; `quote` is the
 * exact supporting line from that source when it differs from the
 * citation's own quote.
 */
export type ArtifactClaim = {
  start: number;
  end: number;
  support: { citationId: string; quote?: string }[];
};

export type ArtifactProvenance = {
  /** Origin of the payload, e.g. "convex:notes", "web:llmlayer", "function:get_weather" */
  source: string;
  /** The query or instruction that produced this artifact */
  query?: string;
  /**
   * The data set a generic `function.result` was read from: the
   * `query_database` index, e.g. "search-todo-and-calendar". A renderer
   * reads it to tell the user's tasks from their notes without parsing rows.
   */
  dataset?: string;
  retrievedAt: number;
  /** 0-1; 1 for exact DB reads, lower for fuzzy/LLM-derived results */
  confidence?: number;
  citations?: ArtifactCitation[];
  /** Human-readable description of the transform that produced this version (e.g. "yearly sum", "user edit: rewrite paragraph 3") */
  transform?: string;
  /** Claim spans of `payload.body` mapped to the citations that support them. */
  claims?: ArtifactClaim[];
  /**
   * Why this version was made: a clause ("Grafana changed its prices"), or,
   * when `transform` is USER_EDIT_TRANSFORM, the user's own request. Only
   * producers that know set it.
   */
  reason?: string;
  /** What this version changed from the previous one. */
  change?: ArtifactChange;
};

export type ChainArtifact<T = unknown> = {
  artifactId: string;
  kind: ArtifactKind;
  version: number;
  title?: string;
  payload: T;
  provenance: ArtifactProvenance;
  /** Artifact IDs this artifact was derived from (lineage graph) */
  derivedFrom?: string[];
  /** Earlier versions, newest first, bounded (see ARTIFACT_REVISION_LIMIT). */
  revisions?: ArtifactRevision<T>[];
  /** Set on conversation copies whose payload lives only in the artifact store. */
  payloadOmitted?: boolean;
  /**
   * The producer's URL bindings (rule Q3) when its payload is a sub-field of
   * fetchedData and would otherwise leave them behind. Unverified here:
   * every consumer that trusts them checks the signature for the user.
   */
  boundUrls?: readonly BoundUrl[];
};

/**
 * What flows between chained functions. `summary` is a compact text digest
 * for prompt consumption; `artifacts` carry the structured data.
 */
export type ChainEnvelope = {
  artifacts: ChainArtifact[];
  summary: string;
};

export type ChainArtifactManifestEntry = {
  artifactId: string;
  kind: ArtifactKind;
  version: number;
  title?: string;
  producedBy?: string;
  producedByActionId?: string;
  source: string;
  transform?: string;
  derivedFrom?: string[];
  payloadCharacters?: number;
};

export type ChainExecutionContext = {
  artifactManifest: ChainArtifactManifestEntry[];
  directPredecessorActionIds: string[];
  directArtifactIds: string[];
};

export function extractArtifactIds(value: unknown): string[] {
  if (!value || typeof value !== "object") return [];
  const record = value as Record<string, unknown>;
  const directIds = Array.isArray(record.artifactIds) ? record.artifactIds : [];
  const artifactIds = Array.isArray(record.artifacts)
    ? record.artifacts.map((artifact) => (
        artifact && typeof artifact === "object"
          ? (artifact as Record<string, unknown>).artifactId
          : undefined
      ))
    : [];

  return Array.from(new Set([...directIds, ...artifactIds]
    .filter((id): id is string => typeof id === "string" && id.trim().length > 0)
    .map((id) => id.trim())));
}

export function generateArtifactId(): string {
  const cryptoObj = typeof globalThis !== "undefined" ? (globalThis as any).crypto : undefined;
  if (cryptoObj?.randomUUID) {
    return `art_${cryptoObj.randomUUID()}`;
  }
  return `art_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * The code `GET /api/artifacts` sends with a 404 only when the silo was read
 * and holds no such artifact for the user. A reference to it will never
 * resolve. Any other failure (a 503, a 404 without this code) may clear up.
 */
export const ARTIFACT_NOT_FOUND_CODE = "ARTIFACT_NOT_FOUND";

/** Whether a failed artifact read means the artifact is gone for good. */
export function isArtifactNotFoundResponse(status: number | undefined, body: unknown): boolean {
  return status === 404
    && Boolean(body)
    && typeof body === "object"
    && (body as { code?: unknown }).code === ARTIFACT_NOT_FOUND_CODE;
}

export function createArtifact<T>(input: {
  kind: ArtifactKind;
  payload: T;
  provenance: Omit<ArtifactProvenance, "retrievedAt"> & { retrievedAt?: number };
  title?: string;
  derivedFrom?: string[];
  artifactId?: string;
}): ChainArtifact<T> {
  return {
    artifactId: input.artifactId || generateArtifactId(),
    kind: input.kind,
    version: 1,
    title: input.title,
    payload: input.payload,
    provenance: {
      ...input.provenance,
      retrievedAt: input.provenance.retrievedAt ?? Date.now(),
    },
    derivedFrom: input.derivedFrom,
  };
}

/**
 * The provenance string on a document-chunk artifact. Chunks live in the
 * Convex silo now, so their artifacts name the silo table as their source.
 */
export const DOCUMENT_CHUNK_ARTIFACT_SOURCE = "convex:documentChunks";
export const NOTE_RECORD_ARTIFACT_SOURCE = "convex:notes";
export const MESSAGE_RECORD_ARTIFACT_SOURCE = "convex:messageRecords";
/**
 * The provenance string on the ranked document search session a
 * `query_database` step ends with (`doc.search-session`). Its results are
 * silo rows the step read: `documentChunks` hits and the hosted mailbox's
 * messages (`documentSearchSessionStep.ts`).
 */
export const DOCUMENT_SEARCH_SESSION_ARTIFACT_SOURCE = "function:query_database";

/** Logical dataset id → artifact kind. Kinds mirror the normalized DB record types. */
const INDEX_KIND_MAP: Record<string, ArtifactKind> = {
  // Retired as an index; the name still resolves so a chained artifact kind
  // stays stable for consumers that recorded it.
  "search-doc-embeddings": "doc.chunk",
  "search-document-list": "doc.ref",
  "search-integrations": "integration.record",
  "search-todo-and-calendar": "calendar.item",
  "search-user-contacts": "contact",
  "search-conversation-history": "conversation.message",
  "search-memory": "memory",
  "search-user-details": "user.profile",
  "search-user-preferences": "user.preferences",
  "search-job-task": "job.task",
  "search-custom-workflow-results": "workflow.result",
  "search-history-docs": "doc.history",
  "search-survey-answers": "survey.answer",
  "search-sync-list": "sync.state",
  "search-notes": "note.record",
};

export function kindForIndex(index: string): ArtifactKind {
  return INDEX_KIND_MAP[index] || `db.${index.replace(/^search-/, "")}`;
}

const SUMMARY_CHARS_PER_ARTIFACT = 4000;
const SUMMARY_TOTAL_CHARS = 16000;
const MAX_CHAIN_ARTIFACTS = 64;
const FETCHED_DATA_METADATA_FIELDS = new Set([
  "action",
  "artifacts",
  "createdDate",
  "id",
  "reason",
  "render",
  "renderGuard",
  "renderSuppressed",
  "score",
  "sourceFunction",
  "tokens",
]);

function excerptPayload(payload: unknown, limit: number): string {
  try {
    const text = typeof payload === "string" ? payload : JSON.stringify(payload);
    if (!text || limit <= 0) return "";
    if (text.length <= limit) return text;
    return limit === 1 ? "…" : `${text.slice(0, limit - 1)}…`;
  } catch {
    return "";
  }
}

/**
 * Compact text digest of artifacts for prompt consumption by downstream
 * functions. Replaces the old JSON.stringify(previousResponse) blob with
 * content-bearing text minus render/meta noise.
 */
export function summarizeArtifacts(artifacts: ChainArtifact[]): string {
  const parts: string[] = [];
  let usedCharacters = 0;
  for (const artifact of artifacts) {
    const separatorCharacters = parts.length > 0 ? 2 : 0;
    const remaining = SUMMARY_TOTAL_CHARS - usedCharacters - separatorCharacters;
    if (remaining <= 0) break;
    const header = `[${artifact.kind}${artifact.title ? `: ${artifact.title}` : ""}]`;
    if (header.length >= remaining) {
      parts.push(header.slice(0, remaining));
      usedCharacters += separatorCharacters + remaining;
      break;
    }
    const body = excerptPayload(
      artifact.payload,
      Math.min(SUMMARY_CHARS_PER_ARTIFACT, remaining - header.length - 1),
    );
    const part = (body ? `${header} ${body}` : header).slice(0, remaining);
    parts.push(part);
    usedCharacters += separatorCharacters + part.length;
  }
  return parts.join("\n\n");
}

function payloadCharacters(payload: unknown): number | undefined {
  try {
    return (typeof payload === "string" ? payload : JSON.stringify(payload)).length;
  } catch {
    return undefined;
  }
}

export function mergeChainArtifacts(...groups: Array<ChainArtifact[] | null | undefined>): ChainArtifact[] {
  const merged: ChainArtifact[] = [];
  const indexById = new Map<string, number>();

  groups.flatMap((group) => Array.isArray(group) ? group : []).forEach((artifact) => {
    if (!artifact || typeof artifact !== "object") return;
    const artifactId = String(artifact.artifactId || "").trim();
    if (!artifactId) return;
    const existingIndex = indexById.get(artifactId);
    if (existingIndex === undefined) {
      indexById.set(artifactId, merged.length);
      merged.push(artifact);
      return;
    }
    if ((artifact.version || 0) >= (merged[existingIndex]?.version || 0)) {
      merged[existingIndex] = artifact;
    }
  });

  // A normal GPT answer chain produces far fewer than this. The cap prevents a
  // pathological long-running graph from repeatedly expanding every payload.
  return merged.length > MAX_CHAIN_ARTIFACTS
    ? merged.slice(merged.length - MAX_CHAIN_ARTIFACTS)
    : merged;
}

export function buildChainArtifactManifest(
  artifacts: ChainArtifact[],
): ChainArtifactManifestEntry[] {
  return mergeChainArtifacts(artifacts).map((artifact) => ({
    artifactId: artifact.artifactId,
    kind: artifact.kind,
    version: artifact.version,
    ...(artifact.title ? { title: artifact.title } : {}),
    source: artifact.provenance?.source || "function:unknown",
    ...(artifact.provenance?.source?.startsWith("function:")
      ? { producedBy: artifact.provenance.source.slice("function:".length) }
      : {}),
    ...(artifact.provenance?.transform ? { transform: artifact.provenance.transform } : {}),
    ...(Array.isArray(artifact.derivedFrom) && artifact.derivedFrom.length > 0
      ? { derivedFrom: artifact.derivedFrom }
      : {}),
    ...(payloadCharacters(artifact.payload) !== undefined
      ? { payloadCharacters: payloadCharacters(artifact.payload) }
      : {}),
  }));
}

export function buildChainEnvelopeFromArtifacts(artifacts: ChainArtifact[]): ChainEnvelope {
  const mergedArtifacts = mergeChainArtifacts(artifacts);
  return {
    artifacts: mergedArtifacts,
    summary: summarizeArtifacts(mergedArtifacts),
  };
}

/**
 * Collect artifacts from a function response (array of { fetchedData } items
 * or a single item). Items without explicit artifacts get a generic
 * `function.result` artifact wrapping their data so the chain envelope is
 * universal without every producer being migrated up front.
 */
export function collectArtifactsFromResponse(
  response: unknown,
  producedBy?: string,
): ChainArtifact[] {
  const items = Array.isArray(response) ? response : [response];
  const artifacts: ChainArtifact[] = [];

  for (const item of items) {
    const fetched = (item as any)?.fetchedData ?? item;
    if (!fetched || typeof fetched !== "object") continue;

    if (Array.isArray(fetched.artifacts) && fetched.artifacts.length > 0) {
      artifacts.push(...fetched.artifacts);
      continue;
    }

    const remainingFetchedData = Object.fromEntries(
      Object.entries(fetched).filter(([key, value]) =>
        !FETCHED_DATA_METADATA_FIELDS.has(key)
        && value !== undefined
        && value !== null
        && value !== "",
      ),
    );
    const fieldPayload = fetched.rawData ?? fetched.data;
    const data = fieldPayload
      ?? (Object.keys(remainingFetchedData).length > 0 ? remainingFetchedData : undefined);
    if (data === undefined || data === null || data === "") continue;

    const artifact = createArtifact({
      kind: "function.result",
      payload: data,
      title: typeof fetched.title === "string" ? fetched.title : undefined,
      provenance: {
        source: producedBy ? `function:${producedBy}` : "function:unknown",
        ...(typeof fetched.index === "string" && fetched.index ? { dataset: fetched.index } : {}),
      },
    });
    // A payload taken from rawData or data leaves fetchedData.boundUrls
    // behind. Carry them, so a later step (a quarantined render) can reuse
    // the links this producer bound. The whole-fetchedData payload already
    // holds them.
    const boundUrls = fieldPayload !== undefined && fieldPayload !== null && Array.isArray(fetched.boundUrls)
      ? fetched.boundUrls.filter(isBoundUrlShape)
      : [];
    artifacts.push(boundUrls.length > 0 ? { ...artifact, boundUrls } : artifact);
  }

  return artifacts;
}

export function buildChainEnvelope(response: unknown, producedBy?: string): ChainEnvelope {
  const artifacts = collectArtifactsFromResponse(response, producedBy);
  return buildChainEnvelopeFromArtifacts(artifacts);
}

/**
 * Compact artifacts for persistence: keep envelope metadata (id, kind,
 * version, provenance, lineage) but drop payloads, which duplicate the
 * content already stored in fetchedData.data/rawData.
 */
export function stripArtifactPayloads(fetchedData: any): any {
  if (!Array.isArray(fetchedData)) return fetchedData;
  return fetchedData.map((item: any) => {
    if (!Array.isArray(item?.artifacts) || item.artifacts.length === 0) return item;
    return {
      ...item,
      artifacts: item.artifacts.map((artifact: any) => {
        const { payload, revisions, ...meta } = artifact || {};
        void payload;
        return {
          ...meta,
          payloadOmitted: true,
          ...(Array.isArray(revisions) && revisions.length > 0
            ? {
                revisions: revisions.map((revision: ArtifactRevision | null | undefined) => {
                  if (!revision) return { payloadOmitted: true };
                  const { payload: revisionPayload, ...revisionMeta } = revision;
                  void revisionPayload;
                  return { ...revisionMeta, payloadOmitted: true };
                }),
              }
            : {}),
        };
      }),
    };
  });
}

/** The transform of a version the user asked for; its `reason` is their request, in their words. */
export const USER_EDIT_TRANSFORM = "edit";

/** What the user asked for when they made this version, or "" when they didn't make it. */
export function userEditRequest(provenance: Pick<ArtifactProvenance, "transform" | "reason"> | undefined): string {
  const transform = typeof provenance?.transform === "string" ? provenance.transform.trim() : "";
  if (transform === USER_EDIT_TRANSFORM) return typeof provenance?.reason === "string" ? provenance.reason.trim() : "";
  // Versions written before the request moved into `reason` carried it in the transform.
  return /^edit:\s*/i.test(transform) ? transform.replace(/^edit:\s*/i, "").trim() : "";
}

/** Why this version was made, as a clause for "updated when ..."; "" for a user edit, which reads as their request instead. */
export function revisionReasonClause(provenance: Pick<ArtifactProvenance, "transform" | "reason"> | undefined): string {
  const transform = typeof provenance?.transform === "string" ? provenance.transform.trim() : "";
  if (transform === USER_EDIT_TRANSFORM || /^edit:/i.test(transform)) return "";
  return typeof provenance?.reason === "string" ? provenance.reason.trim() : "";
}

/**
 * Mint a new version of an artifact in place: same artifactId, bumped
 * version, lineage preserved. Use for edits ("same object, new state");
 * use createArtifact with derivedFrom for genuine derivations.
 *
 * The replaced version is kept as a bounded revision, and the new version
 * records what changed from it. `reason` is why the new version was made
 * ("Grafana changed its prices"), or the user's request for a
 * USER_EDIT_TRANSFORM; pass it only when the caller knows.
 * Claims map offsets of the old body, so they are dropped unless the new
 * version's producer supplies its own.
 */
export function reviseArtifact<T>(
  artifact: ChainArtifact<T>,
  payload: T,
  transform: string,
  options: { reason?: string; claims?: ArtifactClaim[] } = {},
): ChainArtifact<T> {
  const { claims: previousClaims, reason: previousReason, change: previousChange, ...provenance } = artifact.provenance;
  void previousClaims;
  void previousReason;
  void previousChange;
  const reason = normalizeRevisionReason(options.reason);
  const { payloadOmitted, ...rest } = artifact;
  void payloadOmitted;
  return {
    ...rest,
    version: artifact.version + 1,
    payload,
    provenance: {
      ...provenance,
      retrievedAt: Date.now(),
      transform,
      // Without the old payload nothing honest can be said about what changed.
      ...(artifact.payloadOmitted ? {} : { change: describeArtifactChange(artifact.payload, payload) }),
      ...(reason ? { reason } : {}),
      ...(options.claims ? { claims: options.claims } : {}),
    },
    revisions: historyAfterRevising(artifact),
  };
}

/**
 * The artifact as it read at `version`: the current envelope when that is the
 * version asked for, else a kept revision in the same envelope (its payload,
 * time, reason and change; the revisions before it). Claims are dropped, since
 * they map offsets of the current body. Undefined when that version wasn't
 * kept; `payloadOmitted` when it was kept without its payload.
 */
export function artifactAtVersion<T>(artifact: ChainArtifact<T>, version: number): ChainArtifact<T> | undefined {
  if (version === artifact.version) return artifact;
  const revisions = Array.isArray(artifact.revisions) ? artifact.revisions : [];
  const revision = revisions.find((candidate) => candidate && candidate.version === version);
  if (!revision) return undefined;
  // Claims, citations and the change describe the current text only; an earlier
  // version shows its own reason and change, and no line it can't vouch for.
  const provenance = { ...artifact.provenance };
  delete provenance.claims;
  delete provenance.citations;
  delete provenance.reason;
  delete provenance.change;
  delete provenance.transform;
  const rest = { ...artifact };
  delete rest.payloadOmitted;
  const kept = revision.payload !== undefined && !revision.payloadOmitted;
  return {
    ...rest,
    version: revision.version,
    payload: (kept ? revision.payload : undefined) as T,
    ...(kept ? {} : { payloadOmitted: true }),
    provenance: {
      ...provenance,
      retrievedAt: revision.revisedAt,
      ...(revision.transform ? { transform: revision.transform } : {}),
      ...(revision.reason ? { reason: revision.reason } : {}),
      ...(revision.change ? { change: revision.change } : {}),
    },
    revisions: revisions.filter((candidate) => candidate && candidate.version < revision.version),
  };
}

/** Every version the artifact can show, oldest first, the current one last. */
export function keptArtifactVersions(artifact: Pick<ChainArtifact, "version" | "provenance" | "revisions">): Array<{
  version: number;
  at: number;
  current: boolean;
  payloadKept: boolean;
}> {
  const revisions = Array.isArray(artifact.revisions) ? artifact.revisions : [];
  return revisions
    .filter((revision) => revision && revision.version < artifact.version)
    .map((revision) => ({
      version: revision.version,
      at: revision.revisedAt,
      current: false,
      payloadKept: revision.payload !== undefined && !revision.payloadOmitted,
    }))
    .sort((a, b) => a.version - b.version)
    .concat([{ version: artifact.version, at: artifact.provenance.retrievedAt, current: true, payloadKept: true }]);
}
